/**
 * Catch-all route fallback. Whenever React Router can't match the URL —
 * usually because someone deployed an older bundle to production while
 * a new route still lives in the wild (e.g. magic-link emails minted
 * after the new feature shipped but the production deploy was rolled
 * back, see PRD Feb 2026), we want the user to land on something
 * friendly instead of a pure white page. The white page is what
 * triggered the "broken /accept-invite link" report.
 *
 * Special-case: if we're catching `/accept-invite?token=...`, the user
 * is mid-onboarding and the most useful next step is to tell them to
 * use the 6-digit code from the same email on the login page. We send
 * them there directly with `mode=otp` so the passwordless flow is
 * pre-selected.
 */
import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { AlertCircle, KeyRound } from 'lucide-react';

const NotFoundPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isAcceptInvite = location.pathname === '/accept-invite';

  // If a magic-link landed here, the deployed bundle is stale (the
  // route exists in newer builds but not in the one we're serving).
  // Auto-redirect to /login in OTP mode so the user can still get in
  // with the 6-digit code from the same invite email.
  useEffect(() => {
    if (isAcceptInvite) {
      const t = setTimeout(() => {
        navigate('/login?mode=otp', { replace: true });
      }, 2200);
      return () => clearTimeout(t);
    }
  }, [isAcceptInvite, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-appbg dark:from-[#050505] dark:via-[#050505] dark:to-[#0b140f] dark:bg-gradient-to-br px-4">
      <Card className="w-full max-w-md shadow-lg border-slate-200 dark:bg-[#111111] dark:border-white/10" data-testid="not-found-fallback">
        <CardContent className="p-8 text-center">
          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
          <h2 className="text-xl font-semibold text-slate-900 dark:text-[#F5F5F5] mb-1">
            {isAcceptInvite ? 'Taking you to sign-in…' : "We couldn't find that page"}
          </h2>
          <p className="text-sm text-slate-600 dark:text-white/65 mb-5">
            {isAcceptInvite
              ? 'Your one-time login code is still in the invitation email. We\'re sending you to the sign-in page where you can paste it.'
              : 'The page you were looking for has moved or doesn\'t exist.'}
          </p>
          <div className="flex flex-col gap-2">
            {isAcceptInvite && (
              <Button
                onClick={() => navigate('/login?mode=otp', { replace: true })}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                data-testid="not-found-have-code"
              >
                <KeyRound className="w-4 h-4 mr-2" />
                I already have a 6-digit code
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              data-testid="not-found-home"
            >
              Go to home page
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NotFoundPage;
